import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, MapPin, ArrowRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { paths } from '@/utils/path'

interface ProcessItemMetadata {
    source_location_id?: string
    source_location_name?: string
    source_rack_id?: string
    source_rack_code?: string
    source_position_u?: number
    target_rack_id?: string
    target_rack_code?: string
    target_position_u?: number
}

interface ProcessItemsTableProps {
    groupedItems: Record<string, Record<string, any[]>>
    expandedCategories: Set<string>
    expandedNames: Set<string>
    toggleCategory: (category: string) => void
    toggleName: (key: string) => void
    itemType: 'equipment' | 'component'
}

const ProcessItemsTable = ({
    groupedItems,
    expandedCategories,
    expandedNames,
    toggleCategory,
    toggleName,
    itemType,
}: ProcessItemsTableProps) => {
    const basePath =
        itemType === 'equipment'
            ? paths.warehouseEquipment
            : paths.warehouseComponents

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[200px]">
                            Category /{' '}
                            {itemType === 'equipment'
                                ? 'Equipment'
                                : 'Component'}{' '}
                            Name
                        </TableHead>
                        <TableHead className="w-[120px]">Ref</TableHead>
                        <TableHead className="w-[140px]">
                            From Location
                        </TableHead>
                        <TableHead className="w-[90px]">From Rack</TableHead>
                        <TableHead className="w-[140px]">
                            Target Location
                        </TableHead>
                        <TableHead className="w-[90px]">Target Rack</TableHead>
                        <TableHead className="w-[50px]">Qty</TableHead>
                        <TableHead className="w-[80px]">Condition</TableHead>
                        <TableHead className="w-[80px]">Status</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {Object.entries(groupedItems).map(
                        ([category, nameGroups]) => {
                            const isCategoryExpanded =
                                expandedCategories.has(category)
                            const totalQuantity = Object.values(
                                nameGroups
                            ).reduce(
                                (sum, items) =>
                                    sum +
                                    items.reduce(
                                        (itemSum: number, item: any) =>
                                            itemSum + (item.quantity || 1),
                                        0
                                    ),
                                0
                            )
                            return (
                                <>
                                    {/* Category Header Row */}
                                    <TableRow
                                        key={`category-${category}`}
                                        className="bg-muted/50 hover:bg-muted cursor-pointer"
                                        onClick={() => toggleCategory(category)}
                                    >
                                        <TableCell className="w-[200px]">
                                            <div className="flex items-center gap-2 font-semibold">
                                                {isCategoryExpanded ? (
                                                    <ChevronDown className="w-4 h-4" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4" />
                                                )}
                                                <span className="uppercase">
                                                    {category}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="w-[120px]"></TableCell>
                                        <TableCell className="w-[140px]"></TableCell>
                                        <TableCell className="w-[90px]"></TableCell>
                                        <TableCell className="w-[140px]"></TableCell>
                                        <TableCell className="w-[90px]"></TableCell>
                                        <TableCell className="w-[50px] font-semibold">
                                            {totalQuantity}
                                        </TableCell>
                                        <TableCell className="w-[80px]"></TableCell>
                                        <TableCell className="w-[80px]"></TableCell>
                                    </TableRow>

                                    {/* Name Groups */}
                                    {isCategoryExpanded &&
                                        Object.entries(nameGroups).map(
                                            ([name, items]) => {
                                                const nameKey = `${category}-${name}`
                                                const isNameExpanded =
                                                    expandedNames.has(nameKey)
                                                const nameQuantity =
                                                    items.reduce(
                                                        (
                                                            sum: number,
                                                            item: any
                                                        ) =>
                                                            sum +
                                                            (item.quantity ||
                                                                1),
                                                        0
                                                    )
                                                return (
                                                    <>
                                                        {/* Name Header Row */}
                                                        <TableRow
                                                            key={`name-${nameKey}`}
                                                            className="bg-muted/30 hover:bg-muted/40 cursor-pointer"
                                                            onClick={() =>
                                                                toggleName(
                                                                    nameKey
                                                                )
                                                            }
                                                        >
                                                            <TableCell className="w-[200px]">
                                                                <div className="flex items-center gap-2 font-medium pl-6">
                                                                    {isNameExpanded ? (
                                                                        <ChevronDown className="w-4 h-4" />
                                                                    ) : (
                                                                        <ChevronRight className="w-4 h-4" />
                                                                    )}
                                                                    <span>
                                                                        {name}
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="w-[120px]"></TableCell>
                                                            <TableCell className="w-[140px]"></TableCell>
                                                            <TableCell className="w-[90px]"></TableCell>
                                                            <TableCell className="w-[140px]"></TableCell>
                                                            <TableCell className="w-[90px]"></TableCell>
                                                            <TableCell className="w-[50px] font-medium">
                                                                {nameQuantity}
                                                            </TableCell>
                                                            <TableCell className="w-[80px]"></TableCell>
                                                            <TableCell className="w-[80px]"></TableCell>
                                                        </TableRow>

                                                        {/* Detail Rows */}
                                                        {isNameExpanded &&
                                                            items.map(
                                                                (item: any) => {
                                                                    const metadata =
                                                                        item.metadata as
                                                                            | ProcessItemMetadata
                                                                            | undefined
                                                                    // From Location: only show if metadata has source snapshot
                                                                    // For imports, items are new so no "from" location exists
                                                                    const sourceLocationName =
                                                                        metadata?.source_location_name
                                                                    const sourceRackCode =
                                                                        metadata?.source_rack_code
                                                                    const sourcePositionU =
                                                                        metadata?.source_position_u
                                                                    // Target: from metadata only
                                                                    const targetRackCode =
                                                                        metadata?.target_rack_code
                                                                    const targetPositionU =
                                                                        metadata?.target_position_u

                                                                    return (
                                                                        <TableRow
                                                                            key={
                                                                                item.id
                                                                            }
                                                                            className="bg-background"
                                                                        >
                                                                            <TableCell className="w-[200px]">
                                                                                <div className="pl-12">
                                                                                    {item
                                                                                        .item
                                                                                        ?.id ? (
                                                                                        <Link
                                                                                            to={`${basePath}/${item.item.id}`}
                                                                                            className="text-blue-600 hover:underline"
                                                                                        >
                                                                                            {
                                                                                                item
                                                                                                    .item
                                                                                                    .name
                                                                                            }
                                                                                        </Link>
                                                                                    ) : (
                                                                                        item
                                                                                            .item
                                                                                            ?.name ||
                                                                                        '-'
                                                                                    )}
                                                                                </div>
                                                                            </TableCell>
                                                                            <TableCell className="font-medium w-[120px]">
                                                                                {itemType ===
                                                                                'equipment'
                                                                                    ? item
                                                                                          .item
                                                                                          ?.service_tag ||
                                                                                      item
                                                                                          .item
                                                                                          ?.ref ||
                                                                                      '-'
                                                                                    : item
                                                                                          .item
                                                                                          ?.ref ||
                                                                                      '-'}
                                                                            </TableCell>
                                                                            {/* From Location Column */}
                                                                            <TableCell className="w-[140px]">
                                                                                {sourceLocationName ? (
                                                                                    <div className="flex items-center gap-1 text-sm">
                                                                                        <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                                        <span
                                                                                            className="truncate"
                                                                                            title={
                                                                                                sourceLocationName
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                sourceLocationName
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-muted-foreground">
                                                                                        -
                                                                                    </span>
                                                                                )}
                                                                            </TableCell>
                                                                            {/* From Rack / Position Column */}
                                                                            <TableCell className="w-[90px]">
                                                                                {sourceRackCode ||
                                                                                sourcePositionU ? (
                                                                                    <div className="flex items-center gap-1 text-sm">
                                                                                        {sourceRackCode && (
                                                                                            <span className="font-medium">
                                                                                                {
                                                                                                    sourceRackCode
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                        {sourcePositionU && (
                                                                                            <span className="text-orange-600">
                                                                                                U
                                                                                                {
                                                                                                    sourcePositionU
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-muted-foreground">
                                                                                        -
                                                                                    </span>
                                                                                )}
                                                                            </TableCell>
                                                                            {/* Target Location Column - show destination info when available */}
                                                                            <TableCell className="w-[140px]">
                                                                                {targetRackCode ? (
                                                                                    <div className="flex items-center gap-1 text-sm">
                                                                                        <ArrowRight className="w-3 h-3 text-green-600 flex-shrink-0" />
                                                                                        <span className="text-green-600 font-medium">
                                                                                            Transfer
                                                                                        </span>
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-muted-foreground">
                                                                                        -
                                                                                    </span>
                                                                                )}
                                                                            </TableCell>
                                                                            {/* Target Rack / Position Column */}
                                                                            <TableCell className="w-[90px]">
                                                                                {targetRackCode ||
                                                                                targetPositionU ? (
                                                                                    <div className="flex items-center gap-1 text-sm">
                                                                                        {targetRackCode && (
                                                                                            <span className="font-medium text-green-600">
                                                                                                {
                                                                                                    targetRackCode
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                        {targetPositionU && (
                                                                                            <span className="text-green-600">
                                                                                                U
                                                                                                {
                                                                                                    targetPositionU
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-muted-foreground">
                                                                                        -
                                                                                    </span>
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell className="w-[50px]">
                                                                                {item.quantity ||
                                                                                    1}
                                                                            </TableCell>
                                                                            <TableCell className="text-muted-foreground w-[80px]">
                                                                                {item.condition ||
                                                                                    '-'}
                                                                            </TableCell>
                                                                            <TableCell className="w-[80px]">
                                                                                {item.is_returned ? (
                                                                                    <Badge variant="default">
                                                                                        Returned
                                                                                    </Badge>
                                                                                ) : (
                                                                                    <Badge variant="secondary">
                                                                                        {item.item?.status?.toUpperCase() ||
                                                                                            'ACTIVE'}
                                                                                    </Badge>
                                                                                )}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )
                                                                }
                                                            )}
                                                    </>
                                                )
                                            }
                                        )}
                                </>
                            )
                        }
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export default ProcessItemsTable
